import { createSlice, PayloadAction } from "@reduxjs/toolkit";

import { IRouteListItem, Iterable } from "types";

type InitialState = {
  filters: {
    country: string | null;
    duration: string | null;
    roadType: string | null;
    sortByTopRated: boolean;
  };

  totalCount: number;
  isLoading?: boolean;

  isAuthModalVisible: boolean;

  expandedIds: string[];
  activeListIndexes: { [requestID: string]: { [dayIndex: number]: number } };

  list: Iterable<IRouteListItem>[];
};

const initialState: InitialState = {
  totalCount: 0,
  isLoading: true,

  isAuthModalVisible: false,

  expandedIds: [],
  activeListIndexes: {},

  filters: {
    country: null,
    duration: null,
    roadType: null,
    sortByTopRated: false,
  },

  list: [],
};

const slice = createSlice({
  name: "routes-list",
  initialState: initialState,
  reducers: {
    setFilters: (
      state,
      action: PayloadAction<Partial<InitialState["filters"]>>
    ) => {
      state.filters = { ...state.filters, ...action.payload };
    },

    setIsLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },

    setRoutes: (state, action: PayloadAction<Iterable<IRouteListItem>[]>) => {
      state.list = [...action.payload];
    },

    addRoutes: (state, action: PayloadAction<Iterable<IRouteListItem>[]>) => {
      state.list = [...state.list, ...action.payload];
    },

    setExpandedId: (state, action: PayloadAction<string>) => {
      const isExpanded = state.expandedIds.includes(action.payload);
      state.expandedIds = isExpanded
        ? [...state.expandedIds.filter((i) => i !== action.payload)]
        : [...state.expandedIds, action.payload];
    },

    setActiveListIndex: (
      state,
      action: PayloadAction<{
        requestID: string;
        dayIndex: number;
        listIndex: number;
      }>
    ) => {
      const { requestID, dayIndex, listIndex } = action.payload;
      state.activeListIndexes = {
        ...state.activeListIndexes,
        [requestID]: {
          ...state.activeListIndexes[requestID],
          [dayIndex]: listIndex,
        },
      };
    },

    setTotalCount: (state, action: PayloadAction<number>) => {
      state.totalCount = action.payload;
    },

    setIsAuthModalVisible: (state, action: PayloadAction<boolean>) => {
      state.isAuthModalVisible = action.payload;
    },
  },
});

export const {
  reducer,
  actions: {
    setRoutes,
    addRoutes,
    setFilters,
    setIsLoading,
    setExpandedId,
    setTotalCount,
    setActiveListIndex,
    setIsAuthModalVisible,
  },
} = slice;
